import type { FC } from "react"
import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import Fuse from "fuse.js"
import {
	IconCheckOutline18 as Check,
	IconLink2SlashOutline18 as Link2Off,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps, WithQuery } from "./types"
import { getProjectIcon } from "@/lib/projects"
import { useRoadmapFilterState } from "../roadmap-filter-state"

export const ProjectFilterItems: FC<SharedFilterItemsProps & WithQuery> = ({
	onSelect,
	query,
}) => {
	const { projectIds, addProject, removeProject } = useRoadmapFilterState()
	const [projects] = useQuery(queries.projects())

	const filteredProjects = useMemo(() => {
		const unique = Array.from(
			new Map(projects.map((p) => [p.linearProjectId ?? p.id, p])).values(),
		)
		if (!query)
			return unique.sort(
				(a, b) =>
					new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
			)
		const fuse = new Fuse(unique, {
			keys: [
				{ name: "name", weight: 2 },
				{ name: "publicTitle", weight: 2 },
				{ name: "description", weight: 1 },
				{ name: "publicDescription", weight: 1 },
			],
			threshold: 0.4,
		})
		return fuse.search(query).map((r) => r.item)
	}, [projects, query])

	return (
		<>
			<CommandItem
				key="no-project"
				onSelect={() => {
					if (projectIds.includes(null)) {
						removeProject(null)
					} else {
						addProject(null)
					}
					onSelect()
				}}
			>
				<div className="flex w-full items-center gap-2">
					<Link2Off className="h-4 w-4 shrink-0" />
					<span className="flex-1 truncate">No project</span>
					<Check
						className={cn("h-4 w-4 shrink-0", {
							"opacity-100": projectIds.includes(null),
							"opacity-0": !projectIds.includes(null),
						})}
					/>
				</div>
			</CommandItem>
			<hr className="my-2" />

			{filteredProjects.length ? (
				<div className="h-64">
					<FixedSizeList
						height={256}
						width="100%"
						itemCount={filteredProjects.length}
						itemData={filteredProjects}
						itemKey={(index, data) => data[index]?.id ?? index}
						itemSize={36}
						overscanCount={5}
						className="!h-full"
					>
						{({ data, index, style }) => {
							const project = data[index]
							if (!project) return null
							const Icon = getProjectIcon(project.icon)
							const isSelected = projectIds.includes(project.id)
							return (
								<CommandItem
									value={project.id}
									key={project.id}
									style={style}
									onSelect={() => {
										if (isSelected) {
											removeProject(project.id)
										} else {
											addProject(project.id)
										}
										onSelect()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<Icon
											className="h-4 w-4 shrink-0"
											style={
												project.color ? { color: project.color } : undefined
											}
										/>
										<span className="flex-1 truncate">{project.name}</span>
										<Check
											className={cn("h-4 w-4 shrink-0", {
												"opacity-100": isSelected,
												"opacity-0": !isSelected,
											})}
										/>
									</div>
								</CommandItem>
							)
						}}
					</FixedSizeList>
				</div>
			) : (
				<div className="py-4 text-center text-sm text-secondary">
					No projects found
				</div>
			)}
		</>
	)
}
